// client/src/App.js
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleAnalyticsProvider, useGoogleAnalytics } from 'react-ga4-gtag';
import LandingPage from './components/LandingPage';
import TeamSelection from './components/TeamSelection';
import MainGame from './components/MainGame';
import { SocketProvider } from './context/SocketContext';
import { SoundProvider } from './context/SoundContext';
import { fetchUserTeam, getProfile } from './services/api';
import API from './services/api';
import { motion } from 'framer-motion';
import { FaTools, FaClock } from 'react-icons/fa';
// ✅ استيراد دالة حذف التوكن
import { deleteFCMToken } from './services/firebase';

// Global response interceptor to handle authentication errors
API.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    if (status === 401 && !window.location.pathname.includes('/')) {
      localStorage.removeItem('userId');
      localStorage.removeItem('username');
      localStorage.removeItem('continent');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Simple maintenance screen component with translations
const MaintenanceScreen = ({ isEnabled, endTime, messages }) => {
  const { i18n, t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(null);
  const lang = i18n.language;

  const getMessage = () => {
    if (!messages) return t('maintenance.defaultMessage', 'We are currently performing maintenance. Please check back soon.');
    if (lang === 'ar' && messages.ar) return messages.ar;
    if (lang === 'fr' && messages.fr) return messages.fr;
    if (lang === 'es' && messages.es) return messages.es;
    return messages.en || t('maintenance.defaultMessage', 'We are currently performing maintenance. Please check back soon.');
  };

  useEffect(() => {
    if (!endTime) return;
    const end = new Date(endTime);
    const timer = setInterval(() => {
      const diff = end - new Date();
      if (diff <= 0) {
        clearInterval(timer);
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        window.location.reload();
      } else {
        setTimeLeft({
          hours: Math.floor(diff / 3600000),
          minutes: Math.floor((diff % 3600000) / 60000),
          seconds: Math.floor((diff % 60000) / 1000)
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [endTime]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black flex items-center justify-center p-6">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="max-w-md w-full bg-white/10 backdrop-blur-xl rounded-2xl border border-yellow-500/30 p-8 text-center shadow-2xl"
      >
        <motion.div
          animate={{ rotate: [0, 5, -5, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="text-6xl mb-4 text-yellow-400"
        >
          <FaTools />
        </motion.div>
        <h1 className="text-3xl font-bold text-white mb-4">{t('maintenance.title', 'Maintenance Mode')}</h1>
        <p className="text-gray-300 mb-6">{getMessage()}</p>
        {timeLeft && (timeLeft.hours > 0 || timeLeft.minutes > 0 || timeLeft.seconds > 0) && (
          <div className="flex items-center justify-center gap-2 text-yellow-400 bg-black/40 rounded-full px-4 py-2 w-fit mx-auto">
            <FaClock />
            <span className="font-mono text-lg">
              {String(timeLeft.hours).padStart(2,'0')}:{String(timeLeft.minutes).padStart(2,'0')}:{String(timeLeft.seconds).padStart(2,'0')}
            </span>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-6">Soccer Up • The Ultimate Football Manager</p>
      </motion.div>
    </div>
  );
};

// ========== مكون متتبع التحليلات المحسن - يتتبع تغييرات الشاشة ==========
const AnalyticsTracker = ({ currentScreen }) => {
  const gtag = useGoogleAnalytics();
  const previousScreenRef = useRef(null);

  useEffect(() => {
    if (gtag && currentScreen && previousScreenRef.current !== currentScreen) {
      previousScreenRef.current = currentScreen;
      //console.log(`📊 [Analytics] Page view: ${currentScreen}`);
      gtag('event', 'page_view', {
        page_title: currentScreen,
        page_location: window.location.href,
        page_path: window.location.pathname,
        screen_name: currentScreen,
      });
    }
  }, [gtag, currentScreen]);

  return null;
};

// ========== المحتوى الرئيسي للتطبيق ==========
const AppContent = ({ onScreenChange }) => {
  const { t } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsTeamSetup, setNeedsTeamSetup] = useState(false);
  const manualDisconnect = useRef(false);
  const prevScreenRef = useRef(null);

  // Maintenance mode state
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);

  // تحديد الشاشة الحالية وإعلام المكون الأب
  const getCurrentScreen = () => {
    if (maintenanceLoading) return 'loading_maintenance';
    if (maintenance?.isEnabled) return 'maintenance';
    if (loading) return 'loading';
    if (!isAuthenticated) return 'landing';
    if (needsTeamSetup) return 'team_selection';
    return 'main_game';
  };

  // إعلام المكون الأب بتغيير الشاشة
  useEffect(() => {
    const currentScreen = getCurrentScreen();
    if (prevScreenRef.current !== currentScreen) {
      prevScreenRef.current = currentScreen;
      if (onScreenChange) onScreenChange(currentScreen);
    }
  }, [isAuthenticated, loading, maintenance, needsTeamSetup, maintenanceLoading]);

  // Check maintenance status first
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const res = await API.get('/maintenance/status');
        setMaintenance(res.data);
      } catch (err) {
        console.error('Failed to check maintenance status:', err);
        setMaintenance({ isEnabled: false });
      } finally {
        setMaintenanceLoading(false);
      }
    };
    checkMaintenance();
  }, []);

  const loadGameData = async (uid) => {
    try {
      const teamRes = await fetchUserTeam(uid);
      const userTeam = teamRes.data || [];
      setNeedsTeamSetup(userTeam.length !== 6);
      await getProfile(uid);
    } catch (err) {
      console.error('Error loading game data:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('userId');
        localStorage.removeItem('username');
        localStorage.removeItem('continent');
        setIsAuthenticated(false);
        setUserId(null);
        window.location.href = '/';
      } else {
        setNeedsTeamSetup(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (maintenanceLoading) return;
    if (maintenance?.isEnabled) return;

    const savedUserId = localStorage.getItem('userId');
    if (savedUserId) {
      const uid = parseInt(savedUserId);
      setUserId(uid);
      setIsAuthenticated(true);
      loadGameData(uid);
    } else {
      setLoading(false);
    }
  }, [maintenanceLoading, maintenance]);

  const handleAuthSuccess = async (id) => {
    setUserId(id);
    setIsAuthenticated(true);
    localStorage.setItem('userId', id);
    setLoading(true);
    await loadGameData(id);
  };

  const handleTeamConfirmed = async () => {
    setLoading(true);
    await loadGameData(userId);
    setLoading(false);
  };

  // ========== معالج تسجيل الخروج مع حذف توكن FCM ==========
  const handleLogout = async () => {
    manualDisconnect.current = true;
    
    // ✅ حذف توكن FCM الخاص بالمستخدم عند تسجيل الخروج
    if (userId) {
      try {
        await deleteFCMToken(userId);
        //console.log('🗑️ FCM token removed on logout');
      } catch (err) {
        console.warn('⚠️ Could not remove FCM token:', err);
      }
    }

    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('continent');
    setIsAuthenticated(false);
    setUserId(null);
    setNeedsTeamSetup(false);
    manualDisconnect.current = false;
  };

  if (maintenanceLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-2xl">{t('common.loading')}</div>
      </div>
    );
  }

  if (maintenance?.isEnabled) {
    return (
      <MaintenanceScreen
        isEnabled={maintenance.isEnabled}
        endTime={maintenance.endTime}
        messages={maintenance.messages}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-2xl">{t('common.loading')}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LandingPage onAuthSuccess={handleAuthSuccess} />;
  }

  if (needsTeamSetup) {
    return <TeamSelection userId={userId} onTeamConfirmed={handleTeamConfirmed} />;
  }

  return (
    <SocketProvider userId={userId}>
      <SoundProvider>
        <MainGame userId={userId} onLogout={handleLogout} />
      </SoundProvider>
    </SocketProvider>
  );
};

// ========== التطبيق الرئيسي مع تكامل Google Analytics ==========
function App() {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  const [currentScreen, setCurrentScreen] = useState('loading');

  if (!measurementId) {
    console.warn('⚠️ Google Analytics Measurement ID not provided. Analytics disabled.');
    return <AppContent />;
  }

  return (
    <GoogleAnalyticsProvider measurementId={measurementId}>
      <AnalyticsTracker currentScreen={currentScreen} />
      <AppContent onScreenChange={setCurrentScreen} />
    </GoogleAnalyticsProvider>
  );
}

export default App;